/* !!! lightweight-charts ВЕРСИИ 3.8.0 !!! */
import React, { useEffect, useRef, useState } from 'react';
import { createChart, CrosshairMode } from 'lightweight-charts';

const API_BASE = 'https://api-ru.fynex.cc/charts';

const TIMEFRAMES = {
  '1D': { interval: '1m', days: 1 },
  '5D': { interval: '1h', days: 5 },
  '1M': { interval: '12h', days: 30 },
  '3M': { interval: '1d', days: 90 },
  '6M': { interval: '1d', days: 180 },
  'YTD': { interval: '1d', startOfYear: true },
  '1Y': { interval: '1d', days: 365 },
  '5Y': { interval: '1w', days: 365 * 5 },
  'ALL': { interval: '1M', all: true },
};

function ChartWS({ symbol }) {
  const chartContainerRef = useRef();
  const chart = useRef();
  const candleSeries = useRef();
  const volumeSeries = useRef();
  const resizeObserver = useRef();
  
  const [loading, setLoading] = useState(true);
  const [timeframe, setTimeframe] = useState('1D');

  const tf = TIMEFRAMES[timeframe];

  // Вынес логику маппинга, чтобы не дублировать
  const mapKlines = (klines) => {
    const candles = [];
    const volumes = [];
    
    klines.forEach(k => {
      const t = k.time / 1000;
      const open = parseFloat(k.open);
      const close = parseFloat(k.close);
      
      candles.push({
        time: t,
        open: open,
        high: parseFloat(k.high),
        low: parseFloat(k.low),
        close: close,
      });

      volumes.push({
        time: t,
        value: parseFloat(k.volume),
        color: close >= open ? 'rgba(16, 201, 127, 0.5)' : 'rgba(232, 87, 74, 0.5)',
      });
    });
    return { candles, volumes };
  };

  const fetchHistoricalData = async () => {
    setLoading(true);
    try {
      // 1. Для 1D при 1m интервале ВСЕГДА берем full, чтобы не упереться в лимит 1000
      const isLargeHistory = tf.all || tf.startOfYear || tf.days >= 1; 
      
      let url = isLargeHistory 
        ? `${API_BASE}/api/klines/full?symbol=${symbol}&interval=${tf.interval}`
        : `${API_BASE}/api/klines?symbol=${symbol}&interval=${tf.interval}&limit=1000`;
      
      // 2. Если это НЕ полная история, добавляем startTime, 
      // но следим, чтобы данные шли до "сейчас"
      if (!tf.all && !tf.startOfYear) {
        const startTimestamp = Date.now() - tf.days * 24 * 60 * 60 * 1000;
        url += `&startTime=${startTimestamp}`;
      } else if (tf.startOfYear) {
        const startOfYear = new Date(new Date().getFullYear(), 0, 1).getTime();
        url += `&startTime=${startOfYear}`;
      } else if (tf.all) {
        const startOfYear = new Date(new Date().getFullYear()-20, 0, 1).getTime();
        url += `&startTime=${startOfYear}`;
      }

      const res = await fetch(url);
      const json = await res.json();
      let klines = isLargeHistory ? (json.klines || []) : (json || []);

      // 3. СОРТИРОВКА. Библиотека падает или глючит, если время идет не по порядку
      klines.sort((a, b) => a.time - b.time);

      const result = klines.map(k => ({
        time: k.time / 1000,
        open: parseFloat(k.open), 
        high: parseFloat(k.high), 
        low: parseFloat(k.low), 
        close: parseFloat(k.close),
      }));

      const volumes = klines.map(k => ({
        time: k.time / 1000,
        value: parseFloat(k.volume),
        color: parseFloat(k.close) >= parseFloat(k.open) ? 'rgba(16, 201, 127, 0.5)' : 'rgba(232, 87, 74, 0.5)',
      }));

      return { candles: result, volumes: volumes };
    } catch (e) {
      return { candles: [], volumes: [] };
    } finally {
      setLoading(false);
    }
  };

  const updateLastKline = async () => {
    if (!candleSeries.current) return;
    try {
      const response = await fetch(`${API_BASE}/api/klines?symbol=${symbol}&interval=${tf.interval}&limit=1`);
      const data = await response.json();
      const k = Array.isArray(data) ? data[0] : null;

      if (k) {
        const { candles, volumes } = mapKlines([k]);
        candleSeries.current.update(candles[0]);
        if (volumeSeries.current) volumeSeries.current.update(volumes[0]);
      }
    } catch (e) { /* ignore */ }
  };

  useEffect(() => {
    let isMounted = true;
    const container = chartContainerRef.current;

    const setupChart = async () => {
      setLoading(true);
      
      // Инициализация графика
      chart.current = createChart(container, {
        width: container.clientWidth,
        height: container.clientHeight || 420,
        layout: { background: { color: '#090D0C' }, textColor: '#9AAAA3' },
        localization: { dateFormat: 'yyyy-MM-dd' }, 
        grid: {
          horzLines: { visible: true, color: 'rgba(160,205,190,0.07)' },
          vertLines: { visible: true, color: 'rgba(160,205,190,0.07)' },
        },
        crosshair: { mode: CrosshairMode.Normal },
        rightPriceScale: { visible: true, borderColor: 'rgba(160,205,190,0.14)' },
        timeScale: {
          timeVisible: true,
          borderColor: 'rgba(160,205,190,0.14)',
        },
      });

      // Сначала получаем данные, чтобы знать цену и выставить точность
      const { candles, volumes } = await fetchHistoricalData();
      
      if (!isMounted) return;

      const firstPrice = candles.length > 0 ? candles[0].open : 2;
      const precisionOptions = firstPrice < 2 
        ? { priceFormat: { type: 'price', precision: 8, minMove: 0.00000001 } }
        : {};

      candleSeries.current = chart.current.addCandlestickSeries({
        ...precisionOptions,
        upColor: '#10C97F', downColor: '#E8574A',
        borderUpColor: '#10C97F', borderDownColor: '#E8574A',
        wickUpColor: '#10C97F', wickDownColor: '#E8574A',
      });

      volumeSeries.current = chart.current.addHistogramSeries({
        color: 'rgba(16, 201, 127, 0.5)',
        priceFormat: { type: 'volume' },
        priceScaleId: '',
      });
      
      volumeSeries.current.priceScale().applyOptions({
        scaleMargins: { top: 0.85, bottom: 0 },
      });

      if (candles.length > 0) {
        candleSeries.current.setData(candles);
        volumeSeries.current.setData(volumes);
        
        if (tf.all) {
          chart.current.timeScale().fitContent();
        }
      }

      setLoading(false);

      resizeObserver.current = new ResizeObserver(() => {
        if (chart.current && container) {
          chart.current.resize(container.clientWidth, container.clientHeight);
        }
      });
      resizeObserver.current.observe(container);
    };

    setupChart();

    const pollInterval = setInterval(updateLastKline, tf.interval === '1m' ? 2000 : 5000);

    return () => {
      isMounted = false;
      clearInterval(pollInterval);
      if (resizeObserver.current) resizeObserver.current.disconnect();
      if (chart.current) {
        chart.current.remove();
        chart.current = null;
      }
    };
  }, [symbol, timeframe]); // startPrice убран отсюда

  return (
    <div className='chartBlock chart-wrap'>
      {loading && (
        <div className="chart-loading-overlay">
          <div className="loader"></div>
          <span>Loading...</span>
        </div>
      )}
      <div ref={chartContainerRef} className="chart-target" />
      <div className='buttonsChart'>
        {Object.keys(TIMEFRAMES).map((key) => (
          <button
            key={key}
            onClick={() => setTimeframe(key)}
            className={timeframe === key ? 'active' : ''}
          >
            {key}
          </button>
        ))}
      </div>
      <style>{`
        .chartBlock.chart-wrap{ position: relative; background: var(--fx-base); flex: 1; min-height: 0; display: flex; flex-direction: column; }
        .chartBlock .chart-target{ width: 100%; flex: 1; min-height: 0; }
        .chartBlock .chart-loading-overlay{
          position: absolute; top: 0; left: 0; right: 0; bottom: 40px; z-index: 10;
          background: rgba(6,9,8,0.85); display: flex; justify-content: center; align-items: center;
          color: var(--fx-green); font-family: var(--fx-sans); font-size: 13px; gap: 12px;
        }
        .loader { border: 3px solid var(--fx-rule-2); border-top: 3px solid var(--fx-green); border-radius: 50%; width: 22px; height: 22px; animation: spin 1s linear infinite; }
        @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
      `}</style>
    </div>
  );
}

export default ChartWS;









// /* !!! lightweight-charts ВЕРСИИ 3.8.0 !!!   */
// /* npm install lightweight-charts@3.8.0 */

// import React, { useEffect, useRef, useState } from 'react';
// import { createChart, CrosshairMode } from 'lightweight-charts';
// import useWebSocket from 'react-use-websocket';

// const TIMEFRAMES = {
//   '1D': { interval: '1m', days: 1 },
//   '5D': { interval: '1h', days: 5 },
//   '1M': { interval: '1h', days: 30 },
//   '3M': { interval: '1h', days: 90 },
//   '6M': { interval: '2h', days: 180 },
//   'YTD': { interval: '1d', startOfYear: true },
//   '1Y': { interval: '1d', days: 365 },
//   '5Y': { interval: '1w', days: 365 * 5 },
//   'ALL': { interval: '1M', all: true },
// };

// function ChartWS({ symbol }) {
//   const chartContainerRef = useRef();
//   const chart = useRef();
//   const candleSeries = useRef();
//   const volumeSeries = useRef();
//   const resizeObserver = useRef();
//   const currentCandle = useRef(null);
//   const currentCandleStart = useRef(null);
//   const [timeframe, setTimeframe] = useState(() =>
//     symbol.endsWith('BNB') ? '1M' : '1D'
//   );
//   const [startPrice, setStartPrice] = useState(null);

//   const tf = TIMEFRAMES[timeframe];
//   const socketUrl = `wss://stream.binance.com:9443/ws/${symbol.toLowerCase()}@kline_${tf.interval}`;
//   const { lastMessage } = useWebSocket(socketUrl, { shouldReconnect: () => true });

//   const fetchHistoricalData = async () => {
//     const endTime = Date.now();
//     let fromTimestamp;

//     if (tf.startOfYear) {
//       fromTimestamp = new Date(new Date().getFullYear(), 0, 1).getTime();
//     } else if (tf.all) {
//       // Получим самую раннюю доступную свечу
//       const firstCandleRes = await fetch(`https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${tf.interval}&limit=1&startTime=0`);
//       const firstCandle = await firstCandleRes.json();
//       fromTimestamp = firstCandle?.[0]?.[0] ?? 0; // если вдруг пусто — fallback
//     } else {
//       fromTimestamp = endTime - tf.days * 24 * 60 * 60 * 1000;
//     }

//     const intervalMsMap = {
//       '1m': 60_000,
//       '5m': 5 * 60_000,
//       '30m': 30 * 60_000,
//       '1h': 60 * 60_000,
//       '2h': 2 * 60 * 60_000,
//       '1d': 24 * 60 * 60_000,
//       '1w': 7 * 24 * 60 * 60_000,
//       '1M': 30 * 24 * 60 * 60_000,
//     };

//     const intervalMs = intervalMsMap[tf.interval];
//     const limit = 1000;
//     const result = [];
//     const volumeResult = [];
//     let start = fromTimestamp;

//     while (start < endTime) {
//       const url = `https://api.binance.com/api/v3/klines?symbol=${symbol}&interval=${tf.interval}&limit=${limit}&startTime=${start}`;
//       const res = await fetch(url);
//       const data = await res.json();
//       console.log(await data);
//       if (data.length === 0) break;

//       data.forEach(([time, open, high, low, close, volume]) => {
//         const t = Math.floor(time / 1000);
//         const o = +open, h = +high, l = +low, c = +close, v = +volume;

//         const isFlat = o === h && h === l && l === c;
//         const isEmpty = v === 0;
//         const body = Math.abs(c - o);
//         const range = h - l;
//         const isWickTooLong = body / range < 0.1 && range > 0;

//         if (isFlat && isEmpty) return;
//         if (isWickTooLong) return;

//         result.push({ time: t, open: o, high: h, low: l, close: c });
//         volumeResult.push({
//           time: t,
//           value: v,
//           color: c >= o ? 'rgba(14, 203, 129, 0.5)' : 'rgba(239, 83, 80, 0.5)',
//         });
//       });

//       if (data.length < limit) break;
//       start = data[data.length - 1][0] + intervalMs;
//     }

//     return { candles: result, volumes: volumeResult };
//   };

//   useEffect(() => {
//     const setupChart = async () => {
//       if (chart.current) chart.current.remove();
//       const container = chartContainerRef.current;

//       chart.current = createChart(container, {
//         height: 550,
//         layout: { background: { color: '#000000' }, textColor: '#989898' },
//         grid: {
//           horzLines: { visible: true, color: '#1B1B1B' },
//           vertLines: { visible: true, color: '#1B1B1B' },
//         },
//         crosshair: { mode: CrosshairMode.Normal },
//         rightPriceScale: { visible: true },
//         timeScale: {
//           timeVisible: true,
//           borderColor: '#485c7b',
//         },
//       });

//       // Set width to container
//       chart.current.resize(container.clientWidth, 550);

//       candleSeries.current = chart.current.addCandlestickSeries(startPrice != null && startPrice < 2 && {
//         priceFormat: {
//           type: 'price',
//           precision: 8,
//           minMove: 0.00000001
//         }
//       });

//       volumeSeries.current = chart.current.addHistogramSeries({
//         color: 'rgba(14, 203, 129, 0.5)',
//         priceFormat: { type: 'volume' },
//         priceScaleId: '',
//         scaleMargins: { top: 0.85, bottom: 0 },
//       });
      
//       volumeSeries.current.priceScale().applyOptions({
//         scaleMargins: {
//             top: 0.85,   // оставим немного места сверху
//             bottom: 0,  // убираем отступ снизу
//         },
//       });

//       const { candles, volumes } = await fetchHistoricalData();
//       candleSeries.current.setData(candles);
//       volumeSeries.current.setData(volumes);

//       // Resize observer
//       resizeObserver.current = new ResizeObserver(() => {
//         if (chart.current) {
//           chart.current.resize(container.clientWidth, 550);
//         }
//       });

//       resizeObserver.current.observe(container);
//     };

//     setupChart();

//     return () => {
//       resizeObserver.current?.disconnect();
//     };
//   }, [timeframe, startPrice]);

//   useEffect(() => {
//     if (!lastMessage) return;
//     try {
//       const data = JSON.parse(lastMessage.data);
//       const k = data.k;
//       const time = Math.floor(k.t / 1000);

//       const candle = {
//         time,
//         open: +k.o,
//         high: +k.h,
//         low: +k.l,
//         close: +k.c,
//       };
//       const volumeBar = {
//         time,
//         value: +k.v,
//         color: +k.c >= +k.o ? 'rgba(14, 203, 129, 0.5)' : 'rgba(239, 83, 80, 0.5)',
//       };

//       if (startPrice == null) setStartPrice(candle.open);

//       if (currentCandle.current === null || currentCandleStart.current !== k.t) {
//         currentCandleStart.current = k.t;
//         currentCandle.current = candle;
//       } else {
//         currentCandle.current.high = Math.max(currentCandle.current.high, candle.high);
//         currentCandle.current.low = Math.min(currentCandle.current.low, candle.low);
//         currentCandle.current.close = candle.close;
//       }

//       candleSeries.current?.update(currentCandle.current);
//       volumeSeries.current?.update(volumeBar);

//     } catch (e) {
//       console.error('WebSocket parse error:', e);
//     }
//   }, [lastMessage]);

//   return (
//     <div className='chartBlock'>
//       <div ref={chartContainerRef} style={{ width: '100%' }} />
//       <div className='buttonsChart'>
//         {Object.keys(TIMEFRAMES).map((key) => (
//           <button key={key} onClick={() => setTimeframe(key)} style={{ marginRight: 3 }}>
//             {key}
//           </button>
//         ))}
//       </div>
//     </div>
//   );
// }

// export default ChartWS;
